import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import ReactPlayer from "react-player";
import HlsPlayer from './HlsPlayer';
import HlsVerticalPlayer from './HlsVerticalPlayer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import { onVideoPlayFunction } from "../../utils/utils";
import ShortComment from './ShortsComments/ShortComments';
import { likeHandler } from "./service";
import { Pause as PauseIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import Translate from "../../Components/MultiLanguage/Translate";
const HlsPlayerJS = ({
  id, originalId, src, like, comment, projectInfo,
  isActive, isVisible, title, desc,
  appInfo, shareUrl, vanityUrl,
  shouldCloseCommentModal, setShouldCloseCommentModal,
  liked_flag,
  poster,
  handleScroll,
  shortsContainerRef,
  debounce,
  ensureNextFetch,
  showControls = false,
  hideAllOverlays = false,
  onVideoEnd,
  onVideoStart,
  subtitles = []
}) => {
  const [playing, setPlaying] = useState(true);
  const [isCommentModalOpen, setCommentModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(like);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [showIcons, setShowIcons] = useState(false);
  const [pauseIconVisible, setPauseIconVisible] = useState(false);
  const [playIconVisible, setPlayIconVisible] = useState(false);
  const [commentCount, setCommentCount] = useState(comment);
  const [likeColor, setLikeColor] = useState(liked_flag);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descRef = useRef(null);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [loopCount, setLoopCount] = useState(0);
  const [loadingRetries, setLoadingRetries] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const maxRetries = 3;
  const retryDelay = 2000;

  useEffect(() => {
    if (shouldCloseCommentModal) {
      setCommentModalOpen(false);
      setShouldCloseCommentModal(false);
    }
  }, [shouldCloseCommentModal, setShouldCloseCommentModal]);

  useEffect(() => {
    setCommentCount(commentCount);
  }, [commentCount]);

  useEffect(() => {
    if (liked_flag === 'blue') {
      setLiked(true);
      setLikeColor('blue');
    } else {
      setLiked(false);
      setLikeColor('#fff');
    }
  }, [liked_flag]);
  
  useEffect(() => {
    if (isVisible) {
      setPlaying(true);
      setIsMuted(false);
    } else {
      setPlaying(false);
    }
  }, [isVisible]);

  useEffect(() => {
    setLoadingRetries(0);
  }, [src]);

  const checkOverflow = useCallback(() => {
    const el = descRef?.current;
    if (!el) return setIsOverflowing(false);
    const isOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    setIsOverflowing(isOverflow);
  }, [descRef]);
  
  useEffect(() => {
    checkOverflow();

    if (!descRef?.current) return;

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => checkOverflow());
      ro.observe(descRef.current);
    } else {
      window.addEventListener('resize', checkOverflow);
    }

    return () => {
      if (ro && descRef.current) ro.unobserve(descRef.current);
      if (!window.ResizeObserver) window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow, isExpanded]);

  const scrollShortsHandler = () => {
    if (!shortsContainerRef || !shortsContainerRef.current) return;
    try {
      const container = shortsContainerRef.current;
      const currentEl = document.getElementById(id);
      if (!currentEl) {
        container.scrollBy({ top: container.clientHeight, behavior: 'smooth' });
        return;
      }
      let next = currentEl.nextElementSibling;
      while (next && !next.getAttribute('data-instance-id')) {
        next = next.nextElementSibling;
      }
      if (next) {
        const top = next.offsetTop;
        container.scrollTo({ top, behavior: 'smooth' });
      } else {
        container.scrollBy({ top: container.clientHeight, behavior: 'smooth' });
      }
    } catch (e) {
      console.debug('scrollShortsHandler error', e);
    }
  }
  
  const handleError = () => {
    if (loadingRetries < maxRetries) {
      setLoadingRetries(prev => prev + 1);      
      setTimeout(() => {
        if (playerRef.current) {
          setIsMuted(true);
          setPlaying(false);
          setTimeout(() => {setPlaying(true);setIsMuted(false);}, 100);
        }
      }, retryDelay);
    } else {
      scrollShortsHandler();
    }
  };
  
  const onVideoEnded = () => {
    if (onVideoEnd) {
      onVideoEnd();
      return;
    }
    
    setLoopCount((prev) => {
      const next = prev + 1;
      if (next >= 2) {
        try {
          const container = shortsContainerRef.current;
          const currentEl = document.getElementById(id);
          let nextEl = currentEl && currentEl.nextElementSibling;
          if (!nextEl) {
            if (typeof ensureNextFetch === 'function') {
              ensureNextFetch();
            }
          }
        } catch (e) {}
        scrollShortsHandler();
        return 0;
      }
      try {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          const p = videoRef.current.play();
          if (p && p.catch) p.catch(() => {});
        }
      } catch (e) { console.debug(e); }
      return next;
    });
  };
  
  
  
  const handleLike = useCallback(async () => {
    try {
      let newLikedStatus;
      if (liked_flag === 'blue') {
        if (likeColor === '#fff') {
          newLikedStatus = true;
          setLikeColor('blue');
          setLikeCount(prevCount => prevCount + 1);
        } else {
          newLikedStatus = false;
          setLikeColor('#fff');
          setLikeCount(prevCount => prevCount - 1);
        }
      } else {
        newLikedStatus = !liked;
        setLikeColor(newLikedStatus ? 'blue' : '#fff');
        setLikeCount(prevCount => newLikedStatus ? prevCount + 1 : prevCount - 1);
      }
      setLiked(newLikedStatus);
      await likeHandler(originalId || id, newLikedStatus, appInfo);
    } catch (error) {
      console.error('Error updating like status:', error);
    }
  }, [liked, id, originalId, appInfo]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const openCommentModal = useCallback(() => setCommentModalOpen(true), []);
  const closeCommentModal = useCallback(() => setCommentModalOpen(false), []);
  const toggleDescription = useCallback(() => setIsExpanded(prev => !prev), []);
  const openShareModal = useCallback(() => setShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setShareModalOpen(false), []);

  const handleVideoPress = () => {
    if (showControls) {
      if (playing) {
        setPlaying(false);
      }
    } else {
    setPlaying(!playing);
    setShowIcons(true);
    setTimeout(() => setShowIcons(false), 500);
    }
  };

  const shareOptions = useMemo(() => (
    <>
      <FacebookShareButton url={shareUrl} className="share-button facebook">
        <i className="fa-brands fa-facebook-f"></i>
      </FacebookShareButton>
      <TwitterShareButton url={shareUrl} className="share-button twitter">
        <i className="fa-brands fa-twitter"></i>
      </TwitterShareButton>
    </>
  ), [shareUrl]);

  return (
    <div className="video-container" id={id} data-instance-id={id} data-original-id={originalId || id} data-vanity-url={vanityUrl}>
      {src && (
        <div className="video-shorts">
          <div onClick={!showControls ? handleVideoPress : undefined} style={{height:"100%"}}>
            {/* <ReactPlayer
              ref={playerRef}
              url={src}
              playing={isVisible && playing}
              controls={false}
              muted={isMuted}
              loop={false}
              className="react-player"
              width="100%"
              height="100%"
              onClick={handleVideoPress}
              onEnded={onVideoEnded}
              onPlay={() => {setPlaying(true);setIsMuted(false);}}
              onPause={() => {setPlaying(false);setIsMuted(true);}}
              onError={handleError} 
              // playIcon={<PlayArrowIcon />}
              // light={poster}
            /> */}
            {showControls ? (
              <HlsVerticalPlayer
                src={src}
                isVisible={isVisible}
                isMuted={isMuted}
                playing={playing}
                onEnded={onVideoEnded}
                onPlay={() => {setPlaying(true);setIsMuted(false); onVideoStart && onVideoStart();}}
                onPause={() => {setPlaying(false);setIsMuted(true);}}
                poster={poster}
                videoRef={videoRef}
                id={id}
                showControls={showControls}
                subtitles={subtitles}
              />
            ) : (
            <HlsPlayer
              src={src}
              isVisible={isVisible}
              isMuted={isMuted}
              playing={playing}
              onEnded={onVideoEnded}
              onPlay={() => {setPlaying(true);setIsMuted(false); onVideoStart && onVideoStart();}}
              onPause={() => {setPlaying(false);setIsMuted(true);}}
              poster={poster}
              videoRef={videoRef}
              id={id}
              showControls={showControls}
            />
            )}
          </div>
          <div className={`video-overlay ${showIcons ? '' : 'hidden'}`}>
            {playing && <falsePauseIcon className="pause-icon" />}
            {!playing && <PlayArrowIcon className="play-icon" />}
          </div>
          {!hideAllOverlays && (
          <div className="shortsContainer">
            <div className="shortsVideoSideIcons">
              <div className="shortsVideoSideIcon" onClick={handleLike}>
                <ThumbUpIcon style={{ color: likeColor }} />
                <span style={{ color: '#fff' }}>{likeCount}</span>
              </div>
              <div className="shortsVideoSideIcon" onClick={openCommentModal}>
                <InsertCommentIcon />
                <span style={{ color: "#fff" }}>{commentCount}</span>
              </div>
              <div className="shortsVideoSideIcon" onClick={openShareModal}>
                <ShareIcon />
              </div>
            </div>
            <div className="shortsBottom">
              <div className="shortsDesc">
                <p>{title}</p>
              </div>
              <div className="shortDetails">
                <p ref={descRef} style={{ whiteSpace: isExpanded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {desc}
                </p>
                {((isOverflowing) || isExpanded) && (
                  <span className="show-more-button" onClick={toggleDescription}>
                    {isExpanded ? <Translate textKey={'show_less'}/> : <Translate textKey={'show_more'}/>}
                  </span>
                )}
              </div>
            </div>
          </div>
          )}
          {!hideAllOverlays && isCommentModalOpen && projectInfo?.projectConfig?.config?.COMMENTS_REQUIRED === "true" && projectInfo?.projectConfig?.config?.WEBSITE_FIREBASE_JSON_FILE && (
            <div className="comment-modal">
              <ShortComment
                videoId={originalId || id}
                projectInfo={projectInfo}
                isFromShorts={true}
                setCommentModalOpen={setCommentModalOpen}
                setCommentCount={setCommentCount}
              />
            </div>
          )}
          {!hideAllOverlays && isShareModalOpen && (
            <div className="share-modal-overlay" onClick={closeShareModal}>
              <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="share-modal-close" onClick={closeShareModal}>×</button>
                <h3><Translate textKey={'share'}/></h3>
                <div className="share-options">
                  {shareOptions}
                </div>
                <div className="share-url-container">
                  <input type="text" value={shareUrl} readOnly className="share-url-input" />
                  <button onClick={handleCopy} className="copy-button">
                    {isCopied ? <Translate textKey={'copied'}/> : <Translate textKey={'copy'}/>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HlsPlayerJS;
